import type { RadioProgram } from '@/mocks/adminSchedule';
import { dayNames, programTypeColors, programTypeLabels } from '@/mocks/adminSchedule';
import ProgramCard from './ProgramCard';

interface DayViewProps {
  programs: RadioProgram[];
  selectedDay: number;
  onChangeDay: (day: number) => void;
  onEdit?: (program: RadioProgram) => void;
  onDelete?: (program: RadioProgram) => void;
  onDuplicate?: (program: RadioProgram) => void;
  onQuickTimeChange?: (program: RadioProgram) => void;
  onToggleActive?: (program: RadioProgram) => void;
  onCancel?: (program: RadioProgram) => void;
}

const hours: string[] = [];
for (let h = 0; h < 24; h++) {
  hours.push(h.toString().padStart(2, '0') + ':00');
}

export default function DayView({ programs, selectedDay, onChangeDay, onEdit, onDelete, onDuplicate, onQuickTimeChange, onToggleActive, onCancel }: DayViewProps) {
  const dayPrograms = programs.filter((p) => p.dayOfWeek === selectedDay).sort((a, b) => a.startTime.localeCompare(b.startTime));

  return (
    <div>
      {/* Day selector */}
      <div className="flex items-center gap-1 mb-4 overflow-x-auto scrollbar-none pb-1">
        {[0, 1, 2, 3, 4, 5, 6].map((day) => {
          const count = programs.filter((p) => p.dayOfWeek === day).length;
          return (
            <button
              key={day}
              onClick={() => onChangeDay(day)}
              className={`flex flex-col items-center px-3 py-2 rounded-xl text-xs font-semibold cursor-pointer transition-all shrink-0 min-w-[56px] ${
                selectedDay === day
                  ? 'bg-primary-500 text-white'
                  : 'bg-background-50 text-foreground-600 hover:bg-background-100 border border-background-200'
              }`}
            >
              <span className="text-[11px]">{dayNames[day].substring(0, 3)}</span>
              {count > 0 && (
                <span className={`text-[9px] font-bold mt-0.5 ${
                  selectedDay === day ? 'text-white/80' : 'text-foreground-400'
                }`}>
                  {count} prog.
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Timeline view */}
      <div className="space-y-1">
        {hours.map((hour) => {
          const hourPrograms = dayPrograms.filter((p) => {
            const startH = parseInt(p.startTime.split(':')[0]);
            return startH === parseInt(hour.split(':')[0]);
          });

          return (
            <div key={hour} className="flex gap-3 min-h-[40px] group">
              <div className="w-12 shrink-0 pt-1 text-right">
                <span className="text-foreground-400 text-[10px] font-mono font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                  {hour}
                </span>
              </div>
              <div className="flex-1 border-t border-background-200/60 pt-0.5">
                {hourPrograms.length > 0 ? (
                  <div className="space-y-1.5">
                    {hourPrograms.map((p) => (
                      <ProgramCard
                        key={p.id}
                        program={p}
                        compact
                        onEdit={() => onEdit?.(p)}
                        onDelete={() => onDelete?.(p)}
                        onDuplicate={() => onDuplicate?.(p)}
                        onQuickTimeChange={() => onQuickTimeChange?.(p)}
                        onToggleActive={() => onToggleActive?.(p)}
                        onCancel={() => onCancel?.(p)}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="h-6"></div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}